import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from 'react-router';
import { Button, MenuItem, TextField, Popover, IconButton } from "@mui/material";
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import FilterListIcon from "@mui/icons-material/FilterList";
import { DatePicker, DesktopDateTimePicker, renderMultiSectionDigitalClockTimeView, TimePicker } from "@mui/x-date-pickers";
import moment from "moment";
import { useSearchParams } from "react-router-dom";
import { useSearchVariable } from "hooks/search";
import { addColumnFilter, FilterProps, SearchOptions } from "./LargeTable";
import _ from "lodash";

type UrlFilterButtonProps = {
  column: string;
  type: string;
};

type FilterButtonProps = UrlFilterButtonProps & {
  filterProps: FilterProps
}

type InstalledFilter = {
  operator?: string
  value?: string
}

// Filter apply/removing and current status
function dateToString(kind: string, dateVar : Date | null) : string {
  switch(kind) {
    case "date" :     return moment(dateVar).format("YYYY-MM-DD");
    case "datetime" : return moment(dateVar).format("YYYY-MM-DDTHH:mm:ssZ");
    case "time" :     return moment(dateVar).format("HH:mm:ss");
    default:          return ""
  }
}

function stringToDate(kind: string, dateVar: string | undefined | null): Date {
  switch (kind) {
    case "date":      return moment(dateVar, "YYYY-MM-DD", true).toDate();
    case "datetime":  return moment(dateVar, "YYYY-MM-DDTHH:mm:ssZ", true).toDate();
    case "time":      return moment(dateVar, "HH:mm:ss", true).toDate();
    default:          return new Date(Date.now());
  }
};

function hasFilter(searchOptions: SearchOptions, column: string): boolean {
  return (searchOptions.num_filters?.some(f => f.column == column)
    || searchOptions.text_filters?.some(f => f.column == column)
    || searchOptions.date_filters?.some(f => f.column == column)) ?? false
}

const OPERATORS_BY_TYPE = {
  text: [
    { value: "eq", label: "filterOptions.equals" },
    { value: "ct", label: "filterOptions.contains" },
    { value: "be", label: "filterOptions.beginsWith" },
    { value: "en", label: "filterOptions.endsWith" },
  ],
  num: [
    { value: "lt", label: "<" },
    { value: "le", label: "\u2264" },
    { value: "eq", label: "=" },
    { value: "ge", label: "\u2265" },
    { value: "gt", label: ">" },
  ],
  temporal: [
    { value: "lt", label: "<" },
    { value: "le", label: "\u2264" },
    { value: "eq", label: "=" },
    { value: "ge", label: "\u2265" },
    { value: "gt", label: ">" },
  ],
  enum: [
    { value: "eq", label: "=" },
  ],
  none: []
};

const typeMap: Record<string, "text" | "num" | "enum" | "temporal"> = {
  text: "text",
  integer: "num",
  decimal: "num",
  date: "temporal",
  datetime: "temporal",
  time: "temporal",
  boolean: "enum",
};

export const FilterButton = ({ column, type, filterProps }: FilterButtonProps) => {
  const { filter, setFilter } = filterProps
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);
  const id = isOpen ? column + "-filter-popover" : undefined;
  const isTemporal: boolean = ["date", "datetime", "time"].includes(type);
  const activeFilter = hasFilter(filter, column)

  const defaultOperator = {num: "eq", text: "ct", enum: "eq", temporal: "gt"}[typeMap[type]] ?? "";
  const defaultValue : string = {num: "0", text: "", enum: "", temporal: dateToString(type, new Date(Date.now()))}[typeMap[type]] ?? "";

  const [installedFilter, setInstalledFilter] = useState<InstalledFilter>({
    operator: defaultOperator,
    value: defaultValue,
  });

  //handling user actions
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleKeyDown = (event : React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      applyFilter();
    }
  };

  const applyFilter = () => {
    setFilter(curFilter => {
      const newFilter = _.cloneDeep(curFilter)
      const value = installedFilter.value || defaultValue
      const operator = installedFilter.operator || defaultOperator
      addColumnFilter(newFilter, type, column, value, operator)
      return newFilter
    })
    setAnchorEl(null);
  }

  const removeFilter = () => {
    setFilter(curFilter => {
      const newFilter = _.cloneDeep(curFilter)
      switch (type) {
        case 'text':
          newFilter.text_filters = newFilter.text_filters?.filter(f => f.column != column)
          break;
        case 'integer': case 'decimal':
          newFilter.num_filters = newFilter.num_filters?.filter(f => f.column != column)
          break;
        case 'date': case 'datetime': case 'time':
          newFilter.date_filters = newFilter.date_filters?.filter(f => f.column != column)
          break;
        default:
          console.warn("Unrecognized column type: " + type)
      }
      return newFilter
    })
    setInstalledFilter({operator : defaultOperator, value : defaultValue})
    setAnchorEl(null);
  }

  return (
      <>
        <IconButton id={"filterButton-" + column} onClick={handleClick} size="small">
          {activeFilter ? (<FilterListIcon fontSize="small" color={"action"}/>) : (<FilterListOffIcon fontSize="small" color={"disabled"}/>)}
        </IconButton>
        <Popover 
          id={id}
          open={isOpen}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}>
          <div style={{ padding: 8, display: "flex", flexDirection: "row", gap: 12, minWidth: 200 }}>
            <TextField
              size="small"
              select
              label="operator"
              value={installedFilter.operator}
              style={{minWidth: 80}}
              onChange={(e) => setInstalledFilter(filter => ({operator : e.target.value, value: filter.value}))}
            >
              {(OPERATORS_BY_TYPE[typeMap[type] ?? "none"] ?? []).map((op) => (
                <MenuItem key={op.value} value={op.value}>
                  {t(op.label)}
                </MenuItem>
              ))}
            </TextField>

            {!isTemporal && 
              <TextField
              size="small"
              label={t("filterOptions.comparisonValue")}
              value={installedFilter.value}
              onChange={(e) => setInstalledFilter(filter => ({operator : filter.operator, value: e.target.value}))}
              onKeyDown={handleKeyDown}
            />}

            {type === "date" &&
              <DatePicker
                displayWeekNumber={true}
                className="grs-InputDateField"
                desktopModeMediaQuery="@media (not (pointer: coarse))"
                format="dd-MM-yyyy"
                // views={["month", "day"]}
                value={stringToDate("date", installedFilter.value)}
                slotProps={{
                  textField: {
                    size: "small",
                  }
                }}
                onChange={(val, con) => setInstalledFilter(filter => ({operator : filter.operator, value: dateToString("date", val)}))}
              />
            }
            {type === "datetime" && 
              <DesktopDateTimePicker
                displayWeekNumber={true}
                //desktopModeMediaQuery="@media (not (pointer: coarse))"
                format="dd-MM-yyyy HH:mm:ss"
                ampm={false}
                ampmInClock={false}
                value={stringToDate("datetime", installedFilter.value)}
          
                views={["year", "month","day", "hours", "minutes", "seconds"]}
                viewRenderers={{
                  hours: renderMultiSectionDigitalClockTimeView,
                  minutes: renderMultiSectionDigitalClockTimeView,
                  seconds: renderMultiSectionDigitalClockTimeView,
                }}
                slotProps={{
                  textField: {
                    size: "small",
                  }
                }}
          
                onChange={(val, con) => setInstalledFilter(filter => ({operator : filter.operator, value: dateToString("datetime", val)}))}
              />
            }
            {type === "time"&& 
              <TimePicker
                format="HH:mm:ss"
                ampm={false}
                value={stringToDate("time", installedFilter.value)}
                views={["hours", "minutes", "seconds"]} 
                viewRenderers={{
                  hours: renderMultiSectionDigitalClockTimeView,
                  minutes: renderMultiSectionDigitalClockTimeView,
                  seconds:renderMultiSectionDigitalClockTimeView 
                }}

                slotProps={{
                  textField: {
                    size: "small",
                  }
                }}
                onChange={(val, con) => setInstalledFilter(filter => ({operator : filter.operator, value: dateToString("time", val)}))}
              />
            }

            <Button size="small" variant="contained" onClick={applyFilter} color={"primary"} type={"submit"}>
              {t("filterOptions.apply")}
            </Button>
            <Button size="small" variant="contained" onClick={removeFilter} color={"error"}>
              {t("filterOptions.remove")}
            </Button>
          </div>
        </Popover>
      </>
    )
}

export const UrlFilterButton = ({ column, type }: UrlFilterButtonProps) => {
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);
  const id = isOpen ? column + "-filter-popover" : undefined;

  const [_, setSearchParams] = useSearchParams()
  const columnFilter = useSearchVariable(column)

  const activeFilter = Boolean(columnFilter)

  const isTemporal: boolean = ["date", "datetime", "time"].includes(type);

  const defaultOperator = {num: "eq", text: "ct", enum: "eq", temporal: "gt"}[typeMap[type]] ?? "";
  const defaultValue : string = {num: "0", text: "", enum: "", temporal: dateToString(type, new Date(Date.now()))}[typeMap[type]] ?? "";
  
  const [installedFilter, setInstalledFilter] = useState<InstalledFilter>({
    operator: columnFilter?.slice(0, 2) || defaultOperator,
    value: columnFilter?.slice(3) || defaultValue,
  });
  
  //handling user actions
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleKeyDown = (event : React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      applyFilter();
    }
  };

  const applyFilter = () => {
    setSearchParams(params => {
      params.set(column, `${installedFilter.operator}_${installedFilter.value}`)
      return params
    })
    setAnchorEl(null);
  }

  const removeFilter = () => {
    setSearchParams(params => {
      params.delete(column)
      return params
    })
    setInstalledFilter({operator : defaultOperator, value : defaultValue})
    setAnchorEl(null);
  }

  useEffect(() => {
    if (columnFilter) {
      setInstalledFilter({
        operator: columnFilter.slice(0, 2),
        value: columnFilter.slice(3),
      });
    }
  }, [columnFilter]);

  return (
      <>
        <IconButton id={"filterButton-" + column} onClick={handleClick}>
          {activeFilter ? (<FilterListIcon color={"action"}/>) : (<FilterListOffIcon color={"disabled"}/>)}
        </IconButton>
        <Popover 
          id={id}
          open={isOpen}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}>
          <div style={{ padding: 8, display: "flex", flexDirection: "row", gap: 12, minWidth: 200 }}>
            <TextField
              size="small"
              select
              label="operator"
              value={installedFilter.operator}
              style={{minWidth: 80}}
              onChange={(e) => setInstalledFilter(filter => ({operator : e.target.value, value: filter.value}))}
            >
              {(OPERATORS_BY_TYPE[typeMap[type] ?? "none"] ?? []).map((op) => (
                <MenuItem key={op.value} value={op.value}>
                  {t(op.label)}
                </MenuItem>
              ))}
            </TextField>

            {!isTemporal && 
              <TextField
              size="small"
              label={t("filterOptions.comparisonValue")}
              value={installedFilter.value}
              onChange={(e) => setInstalledFilter(filter => ({operator : filter.operator, value: e.target.value}))}
              onKeyDown={handleKeyDown}
            />}

            {type === "date" &&
              <DatePicker
                displayWeekNumber={true}
                className="grs-InputDateField"
                desktopModeMediaQuery="@media (not (pointer: coarse))"
                format="dd-MM-yyyy"
                // views={["month", "day"]}
                value={stringToDate("date", installedFilter.value)}
                slotProps={{
                  textField: {
                    size: "small",
                  }
                }}
                onChange={(val, con) => setInstalledFilter(filter => ({operator : filter.operator, value: dateToString("date", val)}))}
              />
            }
            {type === "datetime" && 
              <DesktopDateTimePicker
                displayWeekNumber={true}
                //desktopModeMediaQuery="@media (not (pointer: coarse))"
                format="dd-MM-yyyy HH:mm:ss"
                ampm={false}
                ampmInClock={false}
                value={stringToDate("datetime", installedFilter.value)}
          
                views={["year", "month","day", "hours", "minutes", "seconds"]}
                viewRenderers={{
                  hours: renderMultiSectionDigitalClockTimeView,
                  minutes: renderMultiSectionDigitalClockTimeView,
                  seconds: renderMultiSectionDigitalClockTimeView,
                }}
                slotProps={{
                  textField: {
                    size: "small",
                  }
                }}
          
                onChange={(val, con) => setInstalledFilter(filter => ({operator : filter.operator, value: dateToString("datetime", val)}))}
              />
            }
            {type === "time"&& 
              <TimePicker
                format="HH:mm:ss"
                ampm={false}
                value={stringToDate("time", installedFilter.value)}
                views={["hours", "minutes", "seconds"]} 
                viewRenderers={{
                  hours: renderMultiSectionDigitalClockTimeView,
                  minutes: renderMultiSectionDigitalClockTimeView,
                  seconds:renderMultiSectionDigitalClockTimeView 
                }}

                slotProps={{
                  textField: {
                    size: "small",
                  }
                }}
                onChange={(val, con) => setInstalledFilter(filter => ({operator : filter.operator, value: dateToString("time", val)}))}
              />
            }

            <Button size="small" variant="contained" onClick={e => applyFilter()} color={"primary"} type={"submit"}>
              {t("filterOptions.apply")}
            </Button>
            <Button size="small" variant="contained" onClick={removeFilter} color={"error"}>
              {t("filterOptions.remove")}
            </Button>
          </div>
        </Popover>
      </>
    )
}
